<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';

const account = useAccount();
const currentRole = useMapGetter('getCurrentRole');

const canManageAccount = computed(() => currentRole.value === 'administrator');

const operationLinks = computed(() => [
  {
    label: 'Início',
    helper: 'KPIs e visão rápida da operação comercial em andamento.',
    to: account.accountScopedRoute('inicio_index'),
  },
  {
    label: 'Clientes',
    helper: 'Entrada principal para histórico do cliente, contexto e jornada.',
    to: account.accountScopedRoute('lais_leads_index'),
  },
  {
    label: 'Visitas',
    helper: 'Agendamento, revisão e acompanhamento operacional das visitas.',
    to: account.accountScopedRoute('visitas_index'),
  },
  {
    label: 'Notificações',
    helper: 'Caixa de alertas do usuário com uma aba dedicada de preferências.',
    to: account.accountScopedRoute('notifications_index'),
  },
  {
    label: 'Meus dados',
    helper: 'Atualize nome, e-mail, telefone e seus atalhos pessoais.',
    to: account.accountScopedRoute('meus_dados_index'),
  },
  {
    label: 'Favoritos e listas',
    helper:
      'Acompanhe as listas salvas pelos clientes e entre em cada coleção.',
    to: account.accountScopedRoute('favoritos_index'),
  },
  {
    label: 'Área do corretor',
    helper: 'Gerencie vitrine, publicação e bloqueios de chave do corretor.',
    to: account.accountScopedRoute('corretor_vitrine_index'),
  },
]);

const adminLinks = computed(() => [
  {
    label: 'Minha Imudei',
    helper: 'Regras de atendimento, IA, WhatsApp e consentimento por cliente.',
    to: account.accountScopedRoute('minha_lais_index'),
  },
  {
    label: 'Perfil da imobiliária',
    helper: 'Identidade, marca, site, CRECI/CNPJ e origens padrão da conta.',
    to: account.accountScopedRoute('imobiliaria_perfil_index'),
  },
  {
    label: 'Empreendimentos',
    helper: 'Cadastre e gerencie a vitrine de imóveis que alimenta a operação.',
    to: account.accountScopedRoute('imoveis_index'),
  },
  {
    label: 'Relatório',
    helper: 'Leia os principais funis e métricas de conversão da conta.',
    to: account.accountScopedRoute('relatorio_index'),
  },
  {
    label: 'Brand Voice',
    helper: 'Ajuste o tom da IA e os limites da comunicação da Imudei.',
    to: account.accountScopedRoute('brand_voice_editor'),
  },
  {
    label: 'Financeiro',
    helper: 'Consulte cobrança, histórico e status do plano ativo.',
    to: account.accountScopedRoute('financeiro_index'),
  },
  {
    label: 'Deletar conta',
    helper: 'Agende, revise ou reverta a exclusão definitiva da conta.',
    to: account.accountScopedRoute('deletar_conta_index'),
  },
]);

const supportLinks = computed(() => [
  {
    label: 'Tickets de suporte',
    helper:
      'Acompanhe seus chamados e os tickets compartilhados da imobiliária.',
    to: account.accountScopedRoute('support_tickets_portal_index'),
  },
  {
    label: 'Suporte Imudei',
    helper: 'Abra a central de ajuda pública com tickets e artigos.',
    href: '/lais/suporte/pt-BR',
  },
  {
    label: 'Guia da Imudei',
    helper: 'Consulte a superfície pública de brand voice e onboarding.',
    href: '/lais/brand-voice',
  },
  {
    label: 'Privacidade',
    helper: 'Veja como seus dados são usados e os controles disponíveis.',
    to: account.accountScopedRoute('privacidade_index'),
  },
  {
    label: 'Termos de uso',
    helper: 'Consulte os termos do app e os links legais relacionados.',
    to: account.accountScopedRoute('termos_index'),
  },
]);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Manual do usuário"
      helper="Um atalho guiado para as áreas principais da operação Imudei dentro do app autenticado."
    />

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]"
      >
        <div class="grid gap-5">
          <LaisCard title="Comece por aqui">
            <div class="grid gap-3">
              <RouterLink
                v-for="item in operationLinks"
                :key="item.label"
                :to="item.to"
                class="rounded-lais border border-lais-border bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-lais-primary hover:shadow-lais-soft"
              >
                <p class="text-[14px] font-semibold text-lais-text">
                  {{ item.label }}
                </p>
                <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                  {{ item.helper }}
                </p>
              </RouterLink>
            </div>
          </LaisCard>

          <LaisCard
            v-if="canManageAccount"
            title="Áreas administrativas e de configuração"
          >
            <div class="grid gap-3 md:grid-cols-2">
              <RouterLink
                v-for="item in adminLinks"
                :key="item.label"
                :to="item.to"
                class="rounded-lais border border-lais-border bg-white px-4 py-3 transition-all hover:border-lais-primary hover:shadow-lais-soft"
              >
                <p class="text-[14px] font-semibold text-lais-text">
                  {{ item.label }}
                </p>
                <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                  {{ item.helper }}
                </p>
              </RouterLink>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="Quando surgir dúvida">
            <div class="grid gap-3">
              <component
                :is="item.to ? RouterLink : 'a'"
                v-for="item in supportLinks"
                :key="item.label"
                :to="item.to"
                :href="item.href"
                :target="item.href ? '_blank' : undefined"
                :rel="item.href ? 'noopener noreferrer' : undefined"
                class="rounded-lais border border-lais-border bg-white px-4 py-3 transition-all hover:border-lais-primary hover:shadow-lais-soft"
              >
                <p class="text-[14px] font-semibold text-lais-text">
                  {{ item.label }}
                </p>
                <p class="mt-1 text-[12px] leading-5 text-lais-text-muted">
                  {{ item.helper }}
                </p>
              </component>
            </div>
          </LaisCard>

          <LaisCard title="Fluxo recomendado do dia a dia">
            <ol class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <li>
                1. Comece por
                <strong class="text-lais-text">Início</strong> para ver o pulso
                da conta e o que está pressionando a operação.
              </li>
              <li>
                2. Vá para <strong class="text-lais-text">Clientes</strong>
                para priorizar contatos, entender contexto e avançar a jornada.
              </li>
              <li>
                3. Use <strong class="text-lais-text">Visitas</strong> e
                <strong class="text-lais-text">Notificações</strong> para fechar
                o operacional sem perder follow-up.
              </li>
              <li v-if="canManageAccount">
                4. Ajustes estruturais ficam em
                <strong class="text-lais-text">Minha Imudei</strong>,
                <strong class="text-lais-text">Brand Voice</strong> e
                <strong class="text-lais-text">Financeiro</strong>.
              </li>
            </ol>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>
</template>
